import { useAppDispatch } from '@app/core/hooks/redux-hooks';
import { calculateBuildStructure } from '@app/core/store/capital-structure-slice';
import { cloneToMutableData, enumKeyByValue } from '@app/shared/helpers';
import { v4 as uuidv4 } from 'uuid';
import { CapitalStructureBuildStructureDto } from '@app/shared/models/contracts/capital-structure-debt-instrument-dto';
import { CalcMethod, EventKey } from '@app/shared/models/contracts/enums/shared-enums';
import {
  CapitalStructureDto,
  EquityRefinancingEventDto,
  ProjectDto,
} from '@app/shared/models/contracts/project-dto';
import { useState } from 'react';
import { UseFormReset, UseFormReturn } from 'react-hook-form';
import { getUpdatedMovements } from '../editEventHelper';

export const useDateChangeBeforeValuationModal = (
  project: ProjectDto,
  capitalStructure: CapitalStructureDto,
  capitalStructureResults: CapitalStructureBuildStructureDto,
  getValues: UseFormReturn<EquityRefinancingEventDto>['getValues'],
  reset: UseFormReset<EquityRefinancingEventDto>,
  capitalStructureKey: string,
  buildStructureEventId: string
) => {
  const [showDateChangeModal, setShowDateChangeModal] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const usesPwerm =
    project.details.calcMethod === enumKeyByValue(CalcMethod, CalcMethod.PWERM_AND_OPM) ||
    project.details.calcMethod === enumKeyByValue(CalcMethod, CalcMethod.PWERM);

  const confirmDateChange = () => {
    const newEventDate = getValues().eventDate;
    const currentMovements = capitalStructure.events[buildStructureEventId]?.movements ?? {};
    const updatedMovements = getUpdatedMovements(newEventDate, currentMovements);

    let cases = cloneToMutableData(project.pwermInput.cases);
    let eventSets = cloneToMutableData(capitalStructure.eventSets);

    const unassignedCases = cases.filter((caseItem) => {
      const eventSetId = caseItem.eventSetId ?? EventKey.EmptyEventSet;
      const eventSet = capitalStructureResults.eventSets[eventSetId];
      return !eventSet?.events.some((event) => event.id === buildStructureEventId);
    });

    unassignedCases.forEach((unassignedCase) => {
      const previousEventSetId = cases.find((c) => c.caseId === unassignedCase.caseId)?.eventSetId;
      const newEventSetId = uuidv4();

      cases = cases.map((caseItem) => {
        if (caseItem.caseId === unassignedCase.caseId) {
          return { ...caseItem, eventSetId: newEventSetId };
        }
        return caseItem;
      });

      eventSets = {
        ...eventSets,
        [newEventSetId]: {
          events: previousEventSetId
            ? [...eventSets[previousEventSetId].events, buildStructureEventId]
            : [buildStructureEventId],
        },
      };
    });

    dispatch(
      calculateBuildStructure({
        project: {
          ...project,
          capitalStructures: {
            ...project.capitalStructures,
            [capitalStructureKey]: {
              ...capitalStructure,
              eventSets,
              events: {
                ...capitalStructure.events,
                [buildStructureEventId]: {
                  ...capitalStructure.events[buildStructureEventId],
                  narrative: getValues().narrative,
                  eventDate: newEventDate,
                  movements: updatedMovements,
                },
              },
            },
          },
          pwermInput: {
            ...project.pwermInput,
            cases,
          },
        },
        ignoreErrors: true,
      })
    );

    setShowDateChangeModal(false);
  };

  const cancelDateChange = () => {
    const originalEventDate = capitalStructure.events[buildStructureEventId].eventDate;
    reset({
      ...getValues(),
      eventDate: originalEventDate,
    });

    setShowDateChangeModal(false);
  };

  const dateIsOnOrBeforeValuationDate = (newDate: string) =>
    new Date(newDate) <= new Date(project.valuationDate);

  const isEventInAllCases = (): boolean => {
    if (!usesPwerm) {
      return true;
    }

    const allCases = project.pwermInput.cases;

    return allCases.every((caseItem) => {
      const eventSetId = caseItem.eventSetId ?? EventKey.EmptyEventSet;
      const eventSet = capitalStructureResults.eventSets[eventSetId];
      return eventSet?.events.some((event) => event.id === buildStructureEventId);
    });
  };

  const displayDateChangeModal = (newDate: string) =>
    dateIsOnOrBeforeValuationDate(newDate) && !isEventInAllCases();

  return {
    showDateChangeModal,
    setShowDateChangeModal,
    confirmDateChange,
    cancelDateChange,
    displayDateChangeModal,
  };
};
